import type { Observable } from 'rxjs'
import { finalize, concatMap } from 'rxjs'
import type { OnDestroy, AfterViewInit, ElementRef } from '@angular/core'
import {
  Component,
  afterRenderEffect,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { RouterLink } from '@angular/router'
import { ProjectAvatarComponent } from '../../../projects/components/project-avatar/project-avatar.component'
import { NotificationService } from '@awork/framework/services/notification-service/notification.service'
import type { Notification } from '@awork/framework/models/notification.model'
import { ViewPersistentService } from '../../../../shared/services/view-persistent/view-persistent.service'
import { EmptyMessageComponent } from '../../../../shared/components/empty-message/empty-message.component'
import { NotificationItemComponent } from '../../components/notification-item/notification-item.component'
import type { NotificationItemAction } from '../../components/notification-item/notification-item.component'
import replace from '@awork/_shared/functions/replace'
import { IconButtonComponent } from '../../../../shared/components/icon-button/icon-button.component'
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component'
import { ListSkeletonComponent } from '../../../../shared/components/list-skeleton/list-skeleton.component'
import { InlineListSearchService } from '../../../../shared/services/inline-list-search/inline-list-search.service'
import { InlineListSearchComponent } from '../../../../shared/components/inline-list-search/inline-list-search.component'
import { BottomSheetComponent } from '../../../../shared/components/bottom-sheet/bottom-sheet.component'
import { CounterBadgeComponent } from '../../../../shared/components/counter-badge/counter-badge.component'
import { IconsDirective } from '@awork/_shared/directives/icons/icons.directive'
import { HapticService } from '../../../../shared/services/haptic-service/haptic.service'
import { ToastService } from '../../../../shared/services/toast-service/toast.service'
import { expandAnimation } from '../../../../shared/animations/expand.animation'
import { TabBarActionDirective } from '../../../../framework/components/tab-bar/tab-bar-action.directive'
import { ContentBlockComponent } from '../../../../shared/components/content-block/content-block.component'
import { ContentItemComponent } from '../../../../shared/components/content-item/content-item.component'
import { ButtonComponent } from '../../../../shared/components/button/button.component'
import { GroupHeaderComponent } from '../../../../shared/components/group-header/group-header.component'
import { DOCUMENT } from '@angular/common'
import { animate, group, style, transition, trigger } from '@angular/animations'
import { OVERLAY_EASE_IN_OUT, OVERLAY_EASE_OUT } from '../../../../shared/animations/overlay-motion'

import { NotificationViewFacade } from './notification-view.facade'
import type { NotificationSection, NotificationView } from './utils'
import { getNotificationSectionId } from './utils'

const MAX_RESTORE_HISTORY_PAGES = 3

/** Ease-out that overshoots slightly, so rows land with a small bounce. Keep in sync with the SCSS. */
const CONVERSATION_SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)'

/** Slides the extra messages of a conversation in and out, like the web inbox. */
const conversationRowAnimation = trigger('conversationRow', [
  transition('void => child', [
    style({
      height: 0,
      opacity: 0,
      transform: 'translateY(-12px) scale(0.97)',
      transformOrigin: 'top center',
      overflow: 'hidden'
    }),
    group([
      animate(`300ms ${OVERLAY_EASE_OUT}`, style({ height: '*' })),
      animate(`380ms ${CONVERSATION_SPRING}`, style({ transform: 'none' })),
      animate('200ms ease-out', style({ opacity: 1 }))
    ])
  ]),
  transition('child => void', [
    style({ height: '*', overflow: 'hidden' }),
    animate(`200ms ${OVERLAY_EASE_IN_OUT}`, style({ height: 0, opacity: 0, transform: 'translateY(-8px)' }))
  ])
])

@Component({
  selector: 'aw-notification-view',
  providers: [NotificationViewFacade],
  templateUrl: './notification-view.component.html',
  styleUrls: ['./notification-view.component.scss'],
  animations: [expandAnimation, conversationRowAnimation],
  imports: [
    CommonModule,
    RouterLink,
    ProjectAvatarComponent,
    EmptyMessageComponent,
    IconButtonComponent,
    ListSkeletonComponent,
    NotificationItemComponent,
    PageHeaderComponent,
    InlineListSearchComponent,
    BottomSheetComponent,
    CounterBadgeComponent,
    IconsDirective,
    GroupHeaderComponent,
    TabBarActionDirective,
    ContentBlockComponent,
    ContentItemComponent,
    ButtonComponent
  ]
})
export class NotificationViewComponent implements AfterViewInit, OnDestroy {
  private notificationService = inject(NotificationService)
  private readonly facade = inject(NotificationViewFacade)
  private viewPersistentService = inject(ViewPersistentService)
  private readonly inlineSearch = inject(InlineListSearchService)
  private readonly haptics = inject(HapticService)
  private readonly toastService = inject(ToastService)
  protected readonly reducedMotion =
    inject(DOCUMENT).defaultView?.matchMedia('(prefers-reduced-motion: reduce)').matches ?? false
  readonly sheet = input(false)
  readonly closed = output<void>()
  private readonly scrollKey = computed(() => (this.sheet() ? 'notifications:sheet' : 'notifications'))
  private readonly restoredScrollAnchor = computed(() =>
    this.viewPersistentService.getListPosition(this.scrollKey() + ':anchor')
  )
  protected readonly restoredNotificationId = computed(() => this.restoredScrollAnchor()?.context)

  readonly loadingSeenConversations = this.facade.loadingSeenConversations
  readonly hasMoreSeenConversations = this.facade.hasMoreSeenConversations
  readonly listNotifications = this.facade.listNotifications
  readonly visibleNotifications = this.facade.visibleNotifications
  readonly sections = this.facade.sections
  readonly expandedConversations = this.facade.expandedConversations
  readonly collapsedSections = this.facade.collapsedSections
  readonly markingSections = this.facade.markingSections
  readonly pendingNotifications = this.facade.pendingNotifications
  readonly searchTerm = this.facade.searchTerm
  protected readonly filterOpen = signal(false)
  private readonly filterSheet = viewChild<BottomSheetComponent>('filterSheet')
  readonly activeView = this.facade.activeView
  readonly showSeenMessages = this.facade.showSeenMessages
  readonly unseenVisibleNotifications = this.facade.unseenVisibleNotifications
  readonly markingAll = this.facade.markingAll
  readonly canMarkAllAsSeen = this.facade.canMarkAllAsSeen
  readonly isLoading = this.facade.isLoading
  readonly showLoading = this.facade.showLoading
  readonly unreadCount = this.facade.unreadCount
  readonly title = computed(() =>
    replace(this.unreadCount() === 1 ? this.translations.titleSingular : this.translations.titleCount, {
      count: this.unreadCount()
    })
  )
  readonly currentUser = this.facade.currentUser
  readonly onlineStatus = this.facade.onlineStatus
  private restoreScrollTimeoutId: number | null = null
  private readonly scrollRestored = signal(false)
  private disableAnimationTimeoutId: number | null = null
  private saveScrollTimeoutId: number | null = null

  readonly container = viewChild<ElementRef<HTMLDivElement>>('container')

  readonly disableAnimation = signal(true)

  protected readonly translations = q.translations.Notifications
  protected readonly commonTranslations = q.translations.common
  protected readonly viewOptions = computed(() => [
    {
      value: 'inbox' as const,
      label: this.translations.tabs.inbox,
      icon: 'inbox',
      count: this.facade.unreadByView().inbox
    },
    {
      value: 'notifications' as const,
      label: this.translations.title,
      icon: 'bell',
      count: this.facade.unreadByView().notifications
    }
  ])
  protected readonly viewTitle = computed(
    () => this.viewOptions().find(view => view.value === this.activeView())!.label
  )
  protected readonly viewSubtitle = computed(() =>
    replace(this.visibleNotifications().length === 1 ? this.translations.titleSingular : this.translations.titleCount, {
      count: this.visibleNotifications().length
    })
  )
  constructor() {
    effect(() => {
      this.facade.sheet.set(this.sheet())

      if (!this.sheet() && this.activeView() === 'inbox' && this.showSeenMessages()) {
        untracked(() => this.loadSeenConversations(true))
      }
    })

    afterRenderEffect(() => {
      this.visibleNotifications()

      if (
        !this.scrollRestored() &&
        (this.facade.failedSeenConversations() || (!this.showLoading() && !this.loadingSeenConversations()))
      ) {
        untracked(() => this.restoreScrollPosition())
      }
    })
  }

  /** Requests history while the facade owns pagination and failure state.
   * @param [reset=false] Starts at the newest history page.
   */
  loadSeenConversations(reset = false): void {
    this.facade.loadSeenConversations(reset)
  }

  /** Loads the next history page after a deliberate tap. */
  loadMoreSeenConversations(): void {
    if (!this.onlineStatus() || this.loadingSeenConversations() || !this.hasMoreSeenConversations()) {
      return
    }

    this.haptics.buttonClick()
    this.loadSeenConversations()
  }

  /** Closes the notifications sheet. */
  closeSheet(): void {
    this.closed.emit()
  }

  /** Gives tap feedback when a section header opens its project. */
  openProjectFeedback(): void {
    this.haptics.buttonClick()
  }

  /** Opens the standard view selector above the notifications page. */
  openFilter(): void {
    this.haptics.buttonClick()
    this.filterOpen.set(true)
  }

  /**
   * Selects a notification view without changing read state.
   * @param {NotificationView} view Selected view.
   */
  selectView(view: NotificationView): void {
    if (view !== this.activeView()) {
      this.haptics.buttonClick()
      this.activeView.set(view)
      this.viewPersistentService.updateActiveView('notifications:view', view)
      this.container()?.nativeElement.scrollTo({ top: 0 })
    }

    this.filterSheet()?.onClose()
  }

  /** Toggles Inbox history without changing any message's read state. */
  toggleSeenMessages(): void {
    this.haptics.buttonClick()
    this.showSeenMessages.update(show => !show)
    this.viewPersistentService.updateActiveView('notifications:show-seen', String(this.showSeenMessages()))
  }

  /** Confirms and marks the notifications in the current view and search as read. */
  markAllAsSeen(): void {
    if (!this.canMarkAllAsSeen() || !window.confirm(this.translations.markAllAsSeenConfirm)) {
      return
    }

    this.markingAll.set(true)
    this.notificationService
      .setNotificationsAsSeen(this.unseenVisibleNotifications())
      .pipe(finalize(() => this.markingAll.set(false)))
      .subscribe({ error: () => this.toastService.show(this.translations.markAsSeenFailed, 'warning') })
  }

  /**
   * Stores the notification list search.
   * @param {string} search Search text.
   */
  searchNotifications(search: string): void {
    this.searchTerm.set(search)
    this.inlineSearch.set('notifications', search)
  }

  /** Enables row animations after the first render. */
  ngAfterViewInit(): void {
    this.disableAnimationTimeoutId = window.setTimeout(() => this.disableAnimation.set(false))
  }

  /** Stops pending history restoration and saves a deliberate scroll after it settles. */
  onListScroll(): void {
    this.stopRestoring()

    if (this.saveScrollTimeoutId !== null) {
      clearTimeout(this.saveScrollTimeoutId)
    }

    this.saveScrollTimeoutId = window.setTimeout(() => this.saveScrollPosition(), 300)
  }

  /** Cancels delayed work and retires an unresolved anchor when the view closes. */
  ngOnDestroy(): void {
    this.stopRestoring()
    this.saveScrollPosition()

    if (this.saveScrollTimeoutId !== null) {
      clearTimeout(this.saveScrollTimeoutId)
    }

    if (this.disableAnimationTimeoutId !== null) {
      clearTimeout(this.disableAnimationTimeoutId)
    }
  }

  /** Stops automatic paging so a missing anchor cannot restart on the next visit. */
  private stopRestoring(): void {
    if (this.restoreScrollTimeoutId !== null) {
      clearTimeout(this.restoreScrollTimeoutId)
      this.restoreScrollTimeoutId = null
    }

    if (!this.scrollRestored()) {
      this.scrollRestored.set(true)
      this.viewPersistentService.updateListPosition(this.scrollKey() + ':anchor', { context: '', offset: 0 })
      this.viewPersistentService.updateScrollPosition(this.scrollKey(), this.container()?.nativeElement.scrollTop ?? 0)
    }
  }

  /** Restores a saved row within three history pages, then falls back to the available content. */
  private restoreScrollPosition(): void {
    if (this.restoreScrollTimeoutId !== null) {
      clearTimeout(this.restoreScrollTimeoutId)
    }

    const persistedScrollPos = this.viewPersistentService.getScrollPosition(this.scrollKey())

    // Allow the restored row's deferred content to render before measuring its position.
    this.restoreScrollTimeoutId = window.setTimeout(() => {
      const container = this.container()?.nativeElement

      if (!container || this.scrollRestored()) {
        return
      }

      const savedAnchor = this.restoredScrollAnchor()
      const anchor = Array.from(container.querySelectorAll<HTMLElement>('.notification-day')).find(
        row => row.dataset.notificationId === this.restoredNotificationId()
      )

      if (
        !anchor &&
        this.restoredNotificationId() &&
        !this.sheet() &&
        this.activeView() === 'inbox' &&
        this.showSeenMessages() &&
        this.hasMoreSeenConversations() &&
        this.onlineStatus() &&
        !this.facade.failedSeenConversations() &&
        this.facade.seenConversationPage() < MAX_RESTORE_HISTORY_PAGES
      ) {
        this.loadSeenConversations()

        return
      }

      const top =
        anchor && savedAnchor
          ? container.scrollTop +
            anchor.getBoundingClientRect().top -
            container.getBoundingClientRect().top -
            savedAnchor.offset
          : persistedScrollPos

      this.scrollRestored.set(true)
      container.scrollTo({ top })

      if (!anchor) {
        this.saveScrollPosition()
      }
    }, 100)
  }

  /** Saves the current offset and visible notification while the list is attached. */
  saveScrollPosition(): void {
    const container = this.container()?.nativeElement
    const restoringInboxHistory =
      !this.sheet() &&
      this.activeView() === 'inbox' &&
      this.showSeenMessages() &&
      !!this.restoredNotificationId() &&
      !this.scrollRestored()

    if (!container?.isConnected || restoringInboxHistory) {
      return
    }

    this.viewPersistentService.updateScrollPosition(this.scrollKey(), container.scrollTop)

    const top = container.getBoundingClientRect().top
    const anchor = Array.from(container.querySelectorAll<HTMLElement>('.notification-day')).find(
      row => row.getBoundingClientRect().bottom > top
    )

    this.viewPersistentService.updateListPosition(this.scrollKey() + ':anchor', {
      context: anchor?.dataset.notificationId ?? '',
      offset: anchor ? anchor.getBoundingClientRect().top - top : 0
    })
  }

  /**
   * Toggles one category without changing its notification read state.
   * @param {string} id Category to toggle.
   */
  toggleSection(id: string): void {
    this.haptics.buttonClick()
    this.collapsedSections.update(current => {
      const collapsed = new Set(current)

      if (collapsed.has(id)) {
        collapsed.delete(id)
      } else {
        collapsed.add(id)
      }

      return collapsed
    })
    this.facade.saveListState()
  }

  /**
   * Marks the notifications represented by one section as read.
   * @param {NotificationSection} section Visible notification category.
   */
  markSectionAsSeen(section: NotificationSection): void {
    const notifications = section.notifications.filter(notification => !notification.seen && !notification.seenOn)

    if (
      !notifications.length ||
      !this.onlineStatus() ||
      this.markingAll() ||
      this.markingSections().has(section.id) ||
      section.pending ||
      !window.confirm(this.translations.markAsSeenConfirm)
    ) {
      return
    }

    this.haptics.buttonClick()
    this.markingSections.update(current => new Set([...current, section.id]))

    const notificationIds = notifications.map(notification => notification.id)

    this.pendingNotifications.update(current => new Set([...current, ...notificationIds]))
    this.notificationService
      .setNotificationsAsSeen(notifications)
      .pipe(
        finalize(() => {
          this.markingSections.update(current => {
            const pending = new Set(current)

            pending.delete(section.id)

            return pending
          })
          this.pendingNotifications.update(current => new Set([...current].filter(id => !notificationIds.includes(id))))
        })
      )
      .subscribe({
        error: () => this.toastService.show(this.translations.markAsSeenFailed, 'warning')
      })
  }

  /**
   * Expands messages for one entity without marking them read.
   * @param {string} id Entity-group identity.
   */
  toggleConversation(id: string): void {
    this.haptics.buttonClick()
    this.expandedConversations.update(current => {
      const expanded = new Set(current)

      if (expanded.has(id)) {
        expanded.delete(id)
      } else {
        expanded.add(id)
      }

      return expanded
    })
    this.facade.saveListState()
  }

  /**
   * Saves a row action, then marks that notification read through the shared store.
   * @param {Notification} notification Row receiving the action.
   * @param {NotificationItemAction} action Selected read or follow action.
   * @param groupedNotifications Messages represented by a collapsed Inbox row.
   */
  onNotificationAction(
    notification: Notification,
    action: NotificationItemAction,
    groupedNotifications = [notification]
  ): void {
    if (
      !this.onlineStatus() ||
      this.markingAll() ||
      this.pendingNotifications().has(notification.id) ||
      this.markingSections().has(getNotificationSectionId(notification, !this.sheet() && this.activeView() === 'inbox'))
    ) {
      return
    }

    if (action.action === 'seen' && !action.navigation && !window.confirm(this.translations.markAsSeenConfirm)) {
      return
    }

    const notifications = action.action === 'seen' && action.navigation ? [notification] : groupedNotifications
    const notificationIds = notifications.map(item => item.id)

    this.pendingNotifications.update(current => new Set([...current, ...notificationIds]))

    let request: Observable<unknown>

    if (action.action === 'seen') {
      request = this.notificationService.setNotificationsAsSeen(notifications)
    } else {
      request = this.notificationService
        .toggleWatchEntity(action.entityType, action.entityId, false)
        .pipe(concatMap(() => this.notificationService.setNotificationsAsSeen(notifications)))
    }

    request
      .pipe(
        finalize(() => {
          this.pendingNotifications.update(current => {
            const pending = new Set(current)

            notificationIds.forEach(id => pending.delete(id))

            return pending
          })
        })
      )
      .subscribe({ error: () => this.toastService.show(this.translations.actionFailed, 'warning') })
  }
}
